import { dateTypeSchema, objectIdSchema, OrderOverallStatus } from '@hero/schema';
import { z } from 'zod';
import { API } from '..';
import {
  AnalyticsCustomerDashboardData,
  AnalyticsCarrierDashboardData,
  AnalyticsDriverDataAPI,
  AnalyticsAppSummaryData,
  MyAnalyticsOverviewData,
} from '../types';
import { orderDateBySchema, orderLensSchema } from './order.api';

/**
 * Which order population to use for "earnings" / "revenue":
 *  - `success`: all non-canceled orders (i.e. unifiedStatus = `success_orders`).
 *    This is the default — what the carrier has earned/booked. Matches
 *    the gross revenue of orders we've committed to, before delivery.
 *  - `delivered`: only orders that have actually been delivered.
 *    Tighter "money in" definition; lags reality by the dropoff date.
 */
export const earningsBasisSchema = z.enum(['success', 'delivered']);
export type EarningsBasis = z.infer<typeof earningsBasisSchema>;

//* driver
export const analyticsDriverAPISchema = z.object({
  /** the driver id that you want to get the analytics for */
  driverId: objectIdSchema,
  /** by default date for the analytics will be this month */
  dateType: dateTypeSchema.default('this_month'),
});

export type AnalyticsDriverAPI = API<
  z.infer<typeof analyticsDriverAPISchema>,
  AnalyticsDriverDataAPI
>;

//* dashboard overview (customer + carrier)
/** @deprecated Use `orderDateBySchema` from order.api.ts instead */
export const analyticsDateBySchema = orderDateBySchema;
export type AnalyticsDateBy = z.infer<typeof orderDateBySchema>;

export const analyticsDashboardOverviewSchema = z.object({
  /** Date field for new orders. Default: 'created' */
  newOrdersDateBy: orderDateBySchema.default('created'),
  /** Date field for delivered orders. Default: 'delivered' */
  deliveredDateBy: orderDateBySchema.default('delivered'),
  /** Date field for canceled orders. Default: 'canceled' */
  canceledDateBy: orderDateBySchema.default('canceled'),
  /**
   * Which order population to use for the `earnings` breakdown.
   * Default: `success` — all non-canceled orders. Gives the
   * "booked revenue" figure: money the org has committed to earning,
   * whether or not the load has actually dropped off yet. Callers
   * who want the tighter "money actually received on delivery"
   * definition can pass
   * `earningsBasis=delivered&earningsDateBy=delivered`.
   */
  earningsBasis: earningsBasisSchema.default('success'),
  /**
   * Date field used to bucket earnings into today/week/month.
   * Default: `created` — pair with `earningsBasis=success` to
   * bucket booked revenue by booking date, which is what the home
   * tile is showing. Pass `delivered` to bucket by the actual
   * dropoff date instead.
   */
  earningsDateBy: orderDateBySchema.default('created'),
});

export type AnalyticsDashboardOverviewReq = z.infer<typeof analyticsDashboardOverviewSchema>;

//* customer overview
export type AnalyticsCustomerOverviewAPI = API<
  AnalyticsDashboardOverviewReq,
  AnalyticsCustomerDashboardData
>;

//* carrier overview
export type AnalyticsCarrierOverviewAPI = API<
  AnalyticsDashboardOverviewReq,
  AnalyticsCarrierDashboardData
>;

//* my overview — role-neutral, lens-aware analytics overview
/**
 * Canonical, role-neutral analytics overview. Powers the home / dashboard
 * "3-card" tiles on every consumer of Hero (carrier-dashboard,
 * customer-dashboard, carrier-app home, and the AI order agent's
 * `get_order_analytics_summary` tool).
 *
 * Accepts an optional `lens` — same shape as the `lens` on
 * `getMyOrders`. When set, the DB query is narrowed to the caller's
 * contract-side (buyer vs seller) via `OrderQueryService.transformQueryAPI`,
 * so the overview counts agree with what the list endpoint returns
 * for the same lens.
 *
 * When omitted (`lens` undefined) or `'none'`, the response is
 * tenant-wide — every Order the caller is a tenant of, regardless
 * of contract side. This matches what the mobile home and web
 * dashboard have always shipped.
 *
 * The response is always the unified {@link MyAnalyticsOverviewData}
 * shape. Seller-only blocks (`earnings`, `needsDriver`, `utilization`,
 * `drivers`) are populated with zero
 * values for buyer lenses; the shared `<AnalyticsOverview>` UI
 * gates on `displayForUser` and won't render them on the customer
 * dashboard.
 */
export const myAnalyticsOverviewSchema = analyticsDashboardOverviewSchema.extend({
  /** Optional role-context lens. Defaults server-side to `none`. */
  lens: orderLensSchema.optional(),
});
export type MyAnalyticsOverviewAPI = API<
  z.infer<typeof myAnalyticsOverviewSchema>,
  MyAnalyticsOverviewData
>;

//* app summary
export const analyticsAppSummaryAPISchema = z.object({
  /** by default date for the analytics will be today */
  dateType: dateTypeSchema.default('today'),
});

export type AnalyticsAppSummaryAPI = API<
  z.infer<typeof analyticsAppSummaryAPISchema>,
  AnalyticsAppSummaryData
>;
